import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { UsageWindowMonth } from '@/services/org-admin';

/*
 * THE reporting period for the admin panel. One control, one URL param, both tabs.
 *
 * There used to be three of these in this file — a shared `?days=` window (7/15/30) for Overview and
 * Usage, plus a per-tab month selection each. They are collapsed into one because the panel now has
 * exactly one vocabulary for time: a calendar period. The day windows went with them.
 *
 * WHAT WAS GIVEN UP, stated because it was real. `?days=` was the only range that let the adoption
 * funnel nest (active in 30d ⊇ 15d ⊇ 7d) and the only one from which a period-over-period delta
 * could be derived on every option, by subtracting a window from the next larger one. Calendar
 * months do not nest and cannot be subtracted, so `this_quarter` and `previous` carry no delta and
 * say so. That was accepted for two reasons: the same figure had to mean the same thing on both
 * tabs, and no reader was reconciling "last 30 days" against a bill that arrives per calendar month.
 *
 * Held in the URL rather than in state so a finding is shareable — `?tab=usage&usageWindow=previous`
 * is a link to what you are looking at, which per-component `useState` could never be. An
 * unrecognised value falls back to the default rather than being passed through to a request the API
 * would reject with a 422.
 */

const MONTH_OPTIONS: UsageWindowMonth[] = ['current', 'previous', 'this_quarter'];

export const USAGE_MONTH_OPTIONS = MONTH_OPTIONS.map((month) => ({
  value: month,
  label: month === 'this_quarter' ? 'This quarter' : month === 'current' ? 'This month' : 'Last month',
}));

export const DEFAULT_USAGE_MONTH: UsageWindowMonth = 'current';

function isUsageWindowMonth(value: string): value is UsageWindowMonth {
  return (MONTH_OPTIONS as string[]).includes(value);
}

/**
 * The selected reporting period, shared by Overview and Usage.
 *
 * Shared on purpose: they are two readings of the same slice of time, so a reader who narrows to
 * last month on one tab and switches to the other must not silently be shown this month. "Filters
 * scope everything below them" applies across the pair, not per tab — which is the argument the
 * retired `?days=` window was already built on, carried over to the period that replaced it.
 *
 * The param keeps its `usageWindow` name so links minted before Overview joined it still resolve.
 */
export function useReportingMonth(): [UsageWindowMonth, (next: UsageWindowMonth) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const raw = searchParams.get('usageWindow');
  const selection = raw && isUsageWindowMonth(raw) ? raw : DEFAULT_USAGE_MONTH;

  const setSelection = useCallback(
    (next: UsageWindowMonth) => {
      const params = new URLSearchParams(searchParams);
      params.set('usageWindow', next);
      // `replace`: changing the period refines the current view, it does not navigate. Pushing would
      // make the back button step through every period the reader tried.
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return [selection, setSelection];
}
